import { useMutation } from 'react-query';
import { useSnackbar } from '@goodfynd/context';
import type { ApiOptionsRequest } from 'types/api';
import type { FormParams } from 'types/shared';

export function useApi<
  TResult = void,
  TParam = void,
  TSearchParams extends FormParams = FormParams,
  TContext = unknown
>({
  getSuccessMessage,
  request,
  showSnackbar = true,
  showErrorSnackbar = true,
  showSuccessSnackbar = true,
  ...options
}: ApiOptionsRequest<TResult, TParam, TSearchParams, TContext>) {
  const { openSnackbarSuccess, openSnackbarWarning } = useSnackbar();

  const { isLoading, mutateAsync, ...response } = useMutation<
    TResult,
    Error,
    TParam,
    TContext
  >({
    mutationFn: request,
    ...options,
    onError(error, ...args) {
      options?.onError?.(error, ...args);

      if (showSnackbar && showErrorSnackbar) {
        console.error('Failed to execute API:', error);
        openSnackbarWarning({
          message: error?.message || 'An unknown error has occurred',
        });
      }
    },
    onSuccess(data, ...args) {
      options?.onSuccess?.(data, ...args);

      if (showSnackbar && showSuccessSnackbar) {
        openSnackbarSuccess({
          message: getSuccessMessage?.(data) ?? 'Success!',
        });
      }
    },
  });

  return {
    ...response,
    isExecuting: isLoading,
    execute: mutateAsync,
  };
}
